import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { STAGES } from "@/lib/constants";
import IdeaCard from "@/components/IdeaCard";
import SubmitIdeaDialog from "@/components/SubmitIdeaDialog";
import { Loader2, AlertTriangle, CheckCircle2, GitBranch } from "lucide-react";

export default function Pipeline() {
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    api.get("/ideas", { params: { status: "active" } })
      .then((r) => setIdeas(r.data))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const breached = ideas.filter((i) => i.health === "breached").length;
  const byStage = (key) => ideas.filter((i) => i.current_stage === key);

  return (
    <div className="p-6">
      <header className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-4xl font-black tracking-tighter">Pipeline Board</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Every idea across six gated stages. Nothing advances without a green checklist and an owner's sign-off.
          </p>
        </div>
        <SubmitIdeaDialog onCreated={load} />
      </header>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <Stat label="Active Ideas" value={ideas.length} icon={GitBranch} />
        <Stat label="SLA Breaches" value={breached} icon={AlertTriangle} tone={breached ? "danger" : "muted"} />
        <Stat label="In Council" value={byStage("ai_council_review").length} icon={CheckCircle2} tone="warning" />
        <Stat label="In Production" value={byStage("production_pod").length} icon={CheckCircle2} tone="success" />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-32 text-muted-foreground">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : (
        <div className="flex gap-4 overflow-x-auto pb-4">
          {STAGES.map((stage) => {
            const items = byStage(stage.key);
            return (
              <div key={stage.key} data-testid={`column-${stage.key}`} className="w-[300px] shrink-0">
                <div className="sticky top-0 bg-background/95 backdrop-blur-md z-10 pb-2">
                  <div className="flex items-center justify-between border-b-2 pb-2" style={{ borderColor: stage.color }}>
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full" style={{ background: stage.color }} />
                      <h3 className="font-heading font-bold text-sm tracking-tight">{stage.name}</h3>
                    </div>
                    <span className="font-mono text-xs text-muted-foreground">{items.length}</span>
                  </div>
                  <p className="font-mono text-[9px] uppercase tracking-wider text-muted-foreground mt-1.5">
                    AI: {stage.ai}
                  </p>
                </div>
                <div className="space-y-2 mt-3">
                  {items.map((idea) => <IdeaCard key={idea.id} idea={idea} />)}
                  {items.length === 0 && (
                    <div className="border border-dashed border-border rounded-sm p-4 text-center">
                      <p className="text-xs text-muted-foreground">No ideas at this gate</p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, icon: Icon, tone = "muted" }) {
  const color = { danger: "#FF3B30", warning: "#F59E0B", success: "#22C55E", muted: "#A1A1AA" }[tone];
  return (
    <div className="border border-border bg-card rounded-sm p-4">
      <div className="flex items-center justify-between">
        <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{label}</span>
        <Icon className="w-4 h-4" style={{ color }} />
      </div>
      <p className="font-heading text-3xl font-black mt-1" style={{ color: tone === "muted" ? "#fff" : color }}>{value}</p>
    </div>
  );
}
